import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  CloudArrowUpIcon,
  PhotoIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { baseImageToOss, generateRandomCode } from '../utils/common';
import { request } from '../utils/request';
import {OSS_BUCKET, OSS_BASE_URL} from '../config';

// 图片类型定义
interface ImageItem {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  type: string;
  isPublic: boolean;
  views: number;
  downloads: number;
  url?: string;
}

// 上传状态类型
interface UploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  result?: ImageItem;
}

const UploadImages: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tmpToken = searchParams.get('tmpToken');
  
  // 状态管理
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [authorization, setAuthorization] = useState<string | null>(null);
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 验证token
  useEffect(() => {
    const validateToken = async () => {
      if (!tmpToken) {
        setIsValidating(false);
        return;
      }

      try {
        const response: any = await request({
          url: 'upload/auth',
          method: 'POST',
          data: { tmpToken }
        });

        setIsValidToken(response.success);
        
        // 如果验证成功，获取并存储 authorization
        if (response.success && response.result?.authorization) {
          setAuthorization(response.result.authorization);
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        setIsValidToken(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [tmpToken]);

  // 获取文件扩展名
  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return '.jpg';
    }
    return filename.substring(lastDotIndex);
  };

  // 处理文件上传
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      alert('Please select image files');
      return;
    }

    // 初始化上传状态
    const initialStatuses: UploadStatus[] = imageFiles.map(file => ({
      file,
      status: 'pending',
      progress: 0
    }));
    setUploadStatuses(initialStatuses);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const newImages: ImageItem[] = [];
      let successCount = 0;

      // 并行处理所有文件
      const uploadPromises = imageFiles.map(async (file, index) => {
        try {
          // 更新状态为上传中
          setUploadStatuses(prev => prev.map((status, i) => 
            i === index ? { ...status, status: 'uploading', progress: 10 } : status
          ));

          // 转换为base64
          const base64String = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
              resolve(event.target?.result as string);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          // 更新进度
          setUploadStatuses(prev => prev.map((status, i) => 
            i === index ? { ...status, progress: 50 } : status
          ));

          // 上传到OSS
          const uploadedName = await baseImageToOss(
            generateRandomCode() + getFileExtension(file.name), 
            base64String, 
            OSS_BUCKET
          );

          // 更新进度
          setUploadStatuses(prev => prev.map((status, i) => 
            i === index ? { ...status, progress: 80 } : status
          ));

          const newImage: ImageItem = {
            id: Date.now() + index,
            name: file.name,
            size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
            uploadDate: new Date().toISOString().split('T')[0],
            type: 'Uploaded',
            isPublic: false,
            views: 0,
            downloads: 0,
            url: `${OSS_BASE_URL}${uploadedName}`
          };

          // 更新状态为成功
          setUploadStatuses(prev => prev.map((status, i) => 
            i === index ? { 
              ...status, 
              status: 'success', 
              progress: 100, 
              data: newImage 
            } : status
          ));

          successCount++;
          setUploadProgress((successCount / imageFiles.length) * 100);

          return newImage;
        } catch (error) {
          console.error(`File upload failed ${file.name}:`, error);
          
          // 更新状态为错误
          setUploadStatuses(prev => prev.map((status, i) => 
            i === index ? { 
              ...status, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Upload failed'
            } : status
          ));

          return null;
        }
      });

      // 等待所有上传完成
      const results = await Promise.all(uploadPromises);
      const successfulImages = results.filter((result): result is ImageItem => result !== null);

      // 提交到数据库
      if (successfulImages.length > 0) {
        try {
          const requestConfig: any = {
            url: 'images/add',
            method: 'POST',
            data: { images: successfulImages }
          };
          
          // 如果有 authorization，添加到请求头
          if (authorization) {
            requestConfig.headers = {
              'Authorization': authorization
            };
          }
          
          const response: any = await request(requestConfig);

          if (response.success) {
            console.log('Images added to database successfully');
          } else {
            console.warn('Failed to add images to database:', response.message);
          }
        } catch (error) {
          console.error('Error adding images to database:', error);
        }
      }

    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // 处理文件选择
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(event.target.files);
  };

  // 处理拖拽上传
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    handleFileUpload(files);
  };

  // 获取状态图标
  const getStatusIcon = (status: UploadStatus['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'uploading':
        return <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
      default:
        return <PhotoIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  // 获取状态文本
  const getStatusText = (status: UploadStatus['status']) => {
    switch (status) {
      case 'success':
        return 'Upload successful';
      case 'error':
        return 'Upload failed';
      case 'uploading':
        return 'Uploading...';
      default:
        return 'Waiting to upload';
    }
  };

  // 加载状态
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Validating access permissions...</p>
        </div>
      </div>
    );
  }

  // Token无效
  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md w-full">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Expired</h1>
          <p className="text-gray-600 mb-6">
            Your access link has expired or is invalid. Please get a new upload link.
          </p>
          <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-500">
            <p>Error Code: TOKEN_EXPIRED</p>
            <p>Please contact administrator for a new upload link</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-md mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900 text-center">Batch Image Upload</h1>
          <p className="text-sm text-gray-600 text-center mt-1">Upload multiple images at once</p>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4">
        {/* 上传区域 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-colors hover:border-gray-400"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleInputChange}
              className="hidden"
              id="image-upload"
              multiple
              disabled={isUploading}
            />
            <label htmlFor="image-upload" className="cursor-pointer">
              <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                {isUploading ? 'Uploading...' : 'Select Images to Upload'}
              </p>
              <p className="text-gray-500 mb-4">
                Click to select or drag images here
              </p>
              <p className="text-sm text-gray-400">
                Supports JPG, PNG, GIF formats
              </p>
            </label>
          </div>
        </div>

        {/* 上传进度 */}
        {isUploading && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Upload Progress</span>
              <span className="text-sm text-gray-500">{Math.round(uploadProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* 上传状态列表 */}
        {uploadStatuses.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Upload Status</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {uploadStatuses.map((uploadStatus, index) => (
                <div key={index} className="p-4">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(uploadStatus.status)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {uploadStatus.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getStatusText(uploadStatus.status)}
                        {uploadStatus.status === 'uploading' && (
                          <span className="ml-2">{uploadStatus.progress}%</span>
                        )}
                      </p>
                      {uploadStatus.error && (
                        <p className="text-xs text-red-500 mt-1">{uploadStatus.error}</p>
                      )}
                    </div>
                    {uploadStatus.status === 'uploading' && (
                      <div className="w-16 bg-gray-200 rounded-full h-1">
                        <div 
                          className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${uploadStatus.progress}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 使用说明 */}
        <div className="bg-blue-50 rounded-lg p-4 mt-6">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Instructions</h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• Select multiple images for batch upload</li>
            <li>• Images will be automatically uploaded to cloud storage</li>
            <li>• Uploaded images can be used in design tools</li>
            <li>• Recommended single image size: under 10MB</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UploadImages;
